import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Clock, Calendar, Globe, CheckCircle, PlayCircle,
  ToggleLeft, ToggleRight, CheckCircle2, XCircle, Loader2,
  Search, Edit3, Sparkles, ArrowRight, Settings as SettingsIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { MarketFlag } from '../lib/MarketFlag';
import { MARKETS } from '../lib/constants';
import QueueTab from './AutopilotQueueTab';
import HistoryTab from './AutopilotHistoryTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Constants ─────────────────────────────────────────────────────────────── */
const MARKET_CODE = {
  Sverige: 'SE', Norge: 'NO', Finland: 'FI', Tyskland: 'DE', Danmark: 'DK', Nederländerna: 'NL',
};
const CODE_TO_DISPLAY = {
  SE: 'Sverige', NO: 'Norge', FI: 'Finland', DE: 'Tyskland', DK: 'Danmark', NL: 'Nederländerna',
};
const DAYS_SE = {
  monday: 'Måndag', tuesday: 'Tisdag', wednesday: 'Onsdag',
  thursday: 'Torsdag', friday: 'Fredag', saturday: 'Lördag', sunday: 'Söndag',
};
const ALL_DAYS = Object.keys(DAYS_SE);
const DAY_MAP = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
};

/* ── Helpers ───────────────────────────────────────────────────────────────── */
function computeNextRun(interval, days, time) {
  const [h, m] = (time || '02:00').split(':').map(Number);
  const now = new Date();
  const next = new Date(now);
  next.setHours(h, m, 0, 0);

  if (interval === 'daily') {
    if (next <= now) next.setDate(next.getDate() + 1);
    return next.toISOString();
  }
  if (interval === 'weekly') {
    const diff = (5 - now.getDay() + 7) % 7 || 7;
    next.setDate(now.getDate() + diff);
    next.setHours(h, m, 0, 0);
    return next.toISOString();
  }
  if (interval === 'custom' && days.length > 0) {
    const targetDays = days.map(d => DAY_MAP[d]).sort((a, b) => a - b);
    const currentDay = now.getDay();
    const schedTime = h * 60 + m;
    const curTime = now.getHours() * 60 + now.getMinutes();
    let minDiff = Infinity;
    for (const td of targetDays) {
      let diff = (td - currentDay + 7) % 7;
      if (diff === 0 && schedTime <= curTime) diff = 7;
      if (diff < minDiff) minDiff = diff;
    }
    next.setDate(now.getDate() + (minDiff === Infinity ? 1 : minDiff));
    next.setHours(h, m, 0, 0);
    return next.toISOString();
  }
  return next.toISOString();
}

async function consumeSse(response, onEvent) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const ev = JSON.parse(line.slice(6));
        onEvent(ev);
      } catch (_) { /* ignore malformed SSE frame */ }
    }
  }
}

/* ── Sub-components ────────────────────────────────────────────────────────── */
function MarketCheckboxGroup({ selected, onChange, label, testIdPrefix = 'ap' }) {
  const allSelected = MARKETS.every(m => selected.includes(m));
  const toggle = m =>
    onChange(selected.includes(m) ? selected.filter(x => x !== m) : [...selected, m]);

  return (
    <div className="field">
      <label className="field-label field-label-row">
        <span className="field-label-left">
          <Globe size={12} className="label-icon" />
          {label}
        </span>
        <button
          type="button"
          className="select-all-markets-btn"
          onClick={() => onChange(allSelected ? [] : [...MARKETS])}
          data-testid={`${testIdPrefix}-select-all-markets`}
        >
          {allSelected ? 'Avmarkera alla' : 'Välj alla'}
        </button>
      </label>
      <div className="market-checkboxes" data-testid={`${testIdPrefix}-market-checkboxes`}>
        {MARKETS.map(mkt => (
          <label
            key={mkt}
            className={`market-checkbox-label${selected.includes(mkt) ? ' market-checkbox-label--on' : ''}`}
            data-testid={`${testIdPrefix}-market-${mkt}`}
          >
            <input type="checkbox" checked={selected.includes(mkt)} onChange={() => toggle(mkt)} />
            <span><MarketFlag market={mkt} /> {mkt}</span>
          </label>
        ))}
      </div>
      {selected.length === 0 && (
        <p className="field-hint" style={{ color: '#ef4444', marginTop: 4 }}>
          Välj minst en marknad
        </p>
      )}
    </div>
  );
}

function FilterRadio({ value, onChange, testIdPrefix = 'ap' }) {
  return (
    <div className="field">
      <div className="field-label">Produktfilter</div>
      <div className="ap-radio-group">
        <label className="ap-radio" data-testid={`${testIdPrefix}-filter-missing`}>
          <input
            type="radio"
            name={`${testIdPrefix}-filter`}
            checked={value === 'missing_description'}
            onChange={() => onChange('missing_description')}
          />
          <span>Endast produkter som saknar beskrivning</span>
        </label>
        <label className="ap-radio" data-testid={`${testIdPrefix}-filter-all`}>
          <input
            type="radio"
            name={`${testIdPrefix}-filter`}
            checked={value === 'all_coming'}
            onChange={() => onChange('all_coming')}
          />
          <span>Alla Coming-produkter</span>
        </label>
      </div>
    </div>
  );
}

const AUTOPILOT_CATEGORIES = [
  'Stödstrumpor',
  'Skor',
  'Kläder',
  'Strumpor & Underkläder',
  'Arbetsredskap',
  'Arbetstillbehör',
  'Namnskyltar',
];

function CategoryDropdown({ value, onChange, testIdPrefix }) {
  return (
    <div className="field">
      <div className="field-label">
        Filtrera på kategori
        <span className="field-hint" style={{ marginLeft: 6 }}>(valfritt)</span>
      </div>
      <select
        className="ap-category-select"
        value={value}
        onChange={e => onChange(e.target.value)}
        data-testid={`${testIdPrefix || 'ap'}-category-select`}
      >
        <option value="">Alla kategorier</option>
        {AUTOPILOT_CATEGORIES.map(cat => (
          <option key={cat} value={cat}>{cat}</option>
        ))}
      </select>
    </div>
  );
}

function WizardStepper({ step }) {
  const steps = [
    ['scan', 'Skanna', Search],
    ['list', 'Välj', CheckCircle2],
    ['scrape', 'Skrapa', Sparkles],
    ['review', 'Granska', Edit3],
    ['generate', 'Generera', PlayCircle],
  ];
  const order = steps.map(s => s[0]);
  const activeIdx = order.indexOf(step);
  return (
    <ul className="ap-wizard-steps" data-testid="ap-wizard-stepper">
      {steps.map(([key, label, Icon], i) => {
        const cls = i === activeIdx
          ? 'ap-wizard-step ap-wizard-step--active'
          : i < activeIdx
            ? 'ap-wizard-step ap-wizard-step--done'
            : 'ap-wizard-step';
        return (
          <li key={key} className={cls} data-testid={`ap-step-${key}`}>
            <span className="ap-wizard-step-num">
              {i < activeIdx ? <CheckCircle size={12} /> : i + 1}
            </span>
            <Icon size={13} />
            {label}
          </li>
        );
      })}
    </ul>
  );
}

/* ── Main Component ────────────────────────────────────────────────────────── */
export default function AutopilotPage() {
  const { user } = useAuth() || {};
  const { categories, templates, vocabulary, model, addUsageLog } = useData();

  /* ── Tab ─────────────────────────────────────────────────────────────────── */
  const [tab, setTab] = useState('schema');

  /* ── Saved schedule ─────────────────────────────────────────────────────── */
  const [schedule, setSchedule]               = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(true);

  /* ── Schedule form ──────────────────────────────────────────────────────── */
  const [interval, setIntervalVal] = useState('weekly');
  const [days, setDays]             = useState(['friday']);
  const [time, setTime]             = useState('02:00');
  const [markets, setMarkets]       = useState([...MARKETS]);
  const [filter, setFilter]         = useState('missing_description');
  const [selCat, setSelCat]         = useState('');
  const [saving, setSaving]         = useState(false);

  /* ── Wizard form ────────────────────────────────────────────────────────── */
  const [wzMarkets, setWzMarkets] = useState([...MARKETS]);
  const [wzFilter, setWzFilter]   = useState('missing_description');
  const [wzCat, setWzCat]         = useState('');

  /* ── Wizard state machine ──────────────────────────────────────────────── */
  // Steps: idle | scanning | list | scraping | review | generating | done
  const [wzStep, setWzStep]         = useState('idle');
  const [wzMessages, setWzMessages] = useState([]);
  const [wzProducts, setWzProducts] = useState([]);   // from /scan
  const [wzSelected, setWzSelected] = useState({});   // { [code]: true }
  const [wzScraped, setWzScraped]   = useState({});   // { [code]: {bullets, source_urls, status} }
  const [wzSummary, setWzSummary]   = useState(null); // {total_skus, total_products}
  const [wzDone, setWzDone]         = useState(null); // {generated, failed, run_id}
  const abortRef = useRef(null);

  /* ── Settings state ─────────────────────────────────────────────────────── */
  const [settings, setSettings] = useState({
    auto_generate: false,
    max_products_per_run: 50,
    auto_scrape: true,
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  /* ── Load existing schedule ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!user || !supabase) { setLoadingSchedule(false); return; }
    supabase
      .from('pipeline_schedules')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setSchedule(data);
          setIntervalVal(data.interval);
          setDays(data.days || []);
          setTime(data.time);
          setMarkets((data.markets || []).map(c => CODE_TO_DISPLAY[c] || c));
          setFilter(data.filter);
          setSelCat(data.categories?.[0] || '');
        }
        setLoadingSchedule(false);
      })
      .catch(() => setLoadingSchedule(false));
  }, [user]);

  /* ── Load Autopilot settings ────────────────────────────────────────────── */
  useEffect(() => {
    if (!user || !supabase) { setSettingsLoaded(true); return; }
    supabase
      .from('autopilot_settings')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setSettings({
            auto_generate: !!data.auto_generate,
            max_products_per_run: data.max_products_per_run ?? 50,
            auto_scrape: data.auto_scrape !== false,
          });
        }
        setSettingsLoaded(true);
      })
      .catch(() => setSettingsLoaded(true));
  }, [user]);

  const saveSettings = useCallback(async (next) => {
    if (!user || !supabase) return;
    setSavingSettings(true);
    const payload = { user_id: user.id, ...next, updated_at: new Date().toISOString() };
    let error = null;
    try {
      const res = await supabase
        .from('autopilot_settings')
        .upsert(payload, { onConflict: 'user_id' });
      error = res?.error || null;
    } catch (e) {
      error = e;
    }
    setSavingSettings(false);
    if (!error) {
      toast.success('Inställningar sparade');
      return;
    }
    // Graceful fallback when V7 migration not yet applied (missing table or
    // supabase-js double body-read TypeError): keep in-memory state, soft-warn.
    const msg = (error.message || '').toLowerCase();
    const code = error.code || '';
    const missingTable = code === '42P01' || msg.includes('relation') || msg.includes('body stream') || msg.includes('does not exist');
    if (missingTable) {
      toast.message('Kör V7__autopilot_settings.sql i Supabase för att spara inställningar mellan sessioner.');
    } else {
      toast.error(`Kunde inte spara inställningar: ${error.message || error}`);
    }
  }, [user]);

  const updateSetting = (key, value) => {
    const next = { ...settings, [key]: value };
    setSettings(next);
    saveSettings(next);
  };

  /* ── Schedule actions ───────────────────────────────────────────────────── */
  const handleToggleActive = async () => {
    if (!schedule || !supabase) return;
    const newActive = !schedule.active;
    const { error } = await supabase
      .from('pipeline_schedules')
      .update({ active: newActive })
      .eq('id', schedule.id);
    if (!error) {
      setSchedule(prev => ({ ...prev, active: newActive }));
      toast.success(newActive ? 'Schema aktiverat' : 'Schema pausat');
    } else {
      toast.error('Kunde inte uppdatera schema');
    }
  };

  const handleSaveSchedule = async () => {
    if (markets.length === 0) { toast.error('Välj minst en marknad'); return; }
    if (interval === 'custom' && days.length === 0) { toast.error('Välj minst en dag'); return; }

    setSaving(true);
    const payload = {
      user_id:    user.id,
      interval,
      days:       interval === 'custom' ? days : [],
      time,
      markets:    markets.map(m => MARKET_CODE[m] || m),
      filter,
      categories: selCat ? [selCat] : null,
      active:     true,
      next_run:   computeNextRun(interval, days, time),
    };

    let result;
    if (schedule) {
      result = await supabase
        .from('pipeline_schedules')
        .update(payload)
        .eq('id', schedule.id)
        .select()
        .maybeSingle();
    } else {
      result = await supabase
        .from('pipeline_schedules')
        .insert(payload)
        .select()
        .maybeSingle();
    }

    setSaving(false);
    if (result.error) {
      toast.error(`Kunde inte spara: ${result.error.message}`);
    } else {
      setSchedule(result.data);
      toast.success('Schema sparat!');
    }
  };

  /* ── Wizard: Step 1 (Scan) ──────────────────────────────────────────────── */
  const handleScan = async () => {
    if (wzMarkets.length === 0) { toast.error('Välj minst en marknad'); return; }
    setWzStep('scanning');
    setWzMessages(['Hämtar Coming-produkter från Norce...']);
    setWzProducts([]);
    setWzSelected({});
    setWzScraped({});
    setWzSummary(null);
    setWzDone(null);

    try {
      const resp = await fetch(`${API_URL}/api/autopilot/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filter: wzFilter,
          categories: wzCat ? [wzCat] : null,
          max_products: settings.max_products_per_run || null,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: resp.status }));
        toast.error(`Skanning misslyckades: ${err.detail || resp.status}`);
        setWzStep('idle');
        return;
      }
      const data = await resp.json();
      setWzProducts(data.products || []);
      setWzSummary({
        total_skus: data.total_skus || 0,
        total_products: data.total_products || 0,
      });
      // Pre-select all products
      const preSel = {};
      (data.products || []).forEach(p => { preSel[p.code] = true; });
      setWzSelected(preSel);
      setWzStep('list');

      // Auto-scrape?
      if (settings.auto_scrape && (data.products || []).length > 0) {
        setWzMessages([`${data.products.length} produkter hittade — startar skrapning automatiskt...`]);
      }
    } catch (e) {
      toast.error(`Anslutningsfel: ${e.message}`);
      setWzStep('idle');
    }
  };

  /* ── Wizard: Step 2 → 3 (Scrape) ────────────────────────────────────────── */
  const handleScrape = async () => {
    const picked = wzProducts.filter(p => wzSelected[p.code]);
    if (picked.length === 0) { toast.error('Välj minst en produkt'); return; }

    setWzStep('scraping');
    setWzMessages([`Skrapar ${picked.length} produkt${picked.length !== 1 ? 'er' : ''}...`]);
    const scrapedMap = {};

    // Get session token for scrape_logs writes (via RLS)
    let accessToken = null;
    if (supabase) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        accessToken = sessionData?.session?.access_token || null;
      } catch (_) { /* ignore */ }
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${API_URL}/api/autopilot/scrape`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          user_id: user?.id || null,
          access_token: accessToken,
          products: picked.map(p => ({
            code: p.code,
            name: p.name,
            ean: (p.eans && p.eans[0]) || '',
            category_id: p.category_id || '',
            manufacturer_code: p.manufacturer_code || '',
          })),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: resp.status }));
        toast.error(`Skrapning misslyckades: ${err.detail || resp.status}`);
        setWzStep('list');
        return;
      }

      await consumeSse(resp, (ev) => {
        if (ev.type === 'status' || ev.type === 'scrape_start') {
          if (ev.message) setWzMessages(prev => [...prev.slice(-9), ev.message]);
        } else if (ev.type === 'scrape_done') {
          scrapedMap[ev.code] = {
            status: ev.status,
            bullets: ev.bullets || '',
            source_urls: ev.source_urls || [],
            confidence: ev.confidence || '',
          };
          if (ev.message) setWzMessages(prev => [...prev.slice(-9), ev.message]);
          setWzScraped({ ...scrapedMap });
        } else if (ev.type === 'done') {
          setWzMessages(prev => [...prev.slice(-9), ev.message || 'Skrapning klar']);
        }
      });

      setWzStep('review');
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error(`Anslutningsfel: ${e.message}`);
        setWzStep('list');
      }
    }
  };

  /* ── Wizard: Step 4 (Generate) ──────────────────────────────────────────── */
  const handleGenerate = async () => {
    if (!supabase) { toast.error('Supabase ej konfigurerat'); return; }
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;
    if (!accessToken) { toast.error('Ingen aktiv session — logga in igen'); return; }

    const picked = wzProducts.filter(p => wzSelected[p.code]);
    const payload = picked
      .filter(p => (wzScraped[p.code]?.bullets || '').trim().length > 0)
      .map(p => ({
        code: p.code,
        name: p.name,
        ean: (p.eans && p.eans[0]) || '',
        category_id: p.category_id || '',
        manufacturer_code: p.manufacturer_code || '',
        bullets: wzScraped[p.code]?.bullets || '',
        source_urls: wzScraped[p.code]?.source_urls || [],
        part_nos: p.part_nos || [],
      }));

    if (payload.length === 0) {
      toast.error('Inga produkter med bullets att generera');
      return;
    }

    setWzStep('generating');
    setWzMessages([`Genererar texter för ${payload.length} produkt${payload.length !== 1 ? 'er' : ''}...`]);

    const marketCodes = wzMarkets.map(m => MARKET_CODE[m] || m);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${API_URL}/api/autopilot/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          user_id: user.id,
          access_token: accessToken,
          markets: marketCodes,
          products: payload,
          triggered_by: 'manual',
          brand_guidelines: templates?.brandGuidelines || '',
          category_templates: templates?.categoryTemplates || {},
          vocabulary: vocabulary || [],
          model: model || 'gemini',
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: resp.status }));
        toast.error(`Generering misslyckades: ${err.detail || resp.status}`);
        setWzStep('review');
        return;
      }

      let generated = 0;
      let failed = 0;
      let runId = null;

      await consumeSse(resp, (ev) => {
        if (ev.type === 'status' || ev.type === 'generating' || ev.type === 'product_start') {
          if (ev.message) setWzMessages(prev => [...prev.slice(-9), ev.message]);
        } else if (ev.type === 'usage') {
          addUsageLog({
            model:        ev.model,
            inputTokens:  ev.input_tokens || 0,
            outputTokens: ev.output_tokens || 0,
            market:       ev.market,
            category:     ev.category,
            source:       'autopilot',
            module:       'autopilot',
          });
        } else if (ev.type === 'product_done') {
          if (ev.status === 'ok') generated += 1;
          else {
            failed += 1;
            if (ev.message) setWzMessages(prev => [...prev.slice(-9), `Fel (${ev.code}): ${ev.message}`]);
          }
        } else if (ev.type === 'done') {
          runId = ev.run_id;
          generated = ev.products_generated ?? generated;
          failed = ev.products_failed ?? failed;
          setWzMessages(prev => [...prev.slice(-9), ev.message]);
        } else if (ev.type === 'error') {
          setWzMessages(prev => [...prev.slice(-9), `Fel: ${ev.message}`]);
        }
      });

      setWzDone({ generated, failed, run_id: runId });
      setWzStep('done');
      toast.success(`Klar — ${generated} produkt${generated !== 1 ? 'er' : ''} i kön`);
    } catch (e) {
      if (e.name !== 'AbortError') {
        toast.error(`Anslutningsfel: ${e.message}`);
        setWzStep('review');
      }
    }
  };

  const handleWizardReset = () => {
    if (abortRef.current) abortRef.current.abort();
    setWzStep('idle');
    setWzMessages([]);
    setWzProducts([]);
    setWzSelected({});
    setWzScraped({});
    setWzSummary(null);
    setWzDone(null);
  };

  /* ── Derived ────────────────────────────────────────────────────────────── */
  const formatWhen = () => {
    if (!schedule) return null;
    const t = schedule.time || '02:00';
    if (schedule.interval === 'daily') return `Dagligen ${t}`;
    if (schedule.interval === 'weekly') return `Veckovis ${t}`;
    if (schedule.interval === 'custom' && schedule.days?.length) {
      const short = { monday: 'Mån', tuesday: 'Tis', wednesday: 'Ons', thursday: 'Tor', friday: 'Fre', saturday: 'Lör', sunday: 'Sön' };
      return `${schedule.days.map(d => short[d] || d).join(', ')} ${t}`;
    }
    return t;
  };
  const scheduleWhen    = formatWhen();
  const scheduleMarkets = (schedule?.markets || []).map(c => CODE_TO_DISPLAY[c] || c).join(', ');

  const selectedCount = Object.values(wzSelected).filter(Boolean).length;
  const allProductsSelected = wzProducts.length > 0 && selectedCount === wzProducts.length;

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="page-scroll">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Autopilot</h1>
          <p className="page-subtitle">
            Automatiserad AI-pipeline — hämtar Coming-produkter, genererar texter och publicerar till Norce.
          </p>
        </div>
        {schedule && (
          <span
            className={`ap-status-badge ${schedule.active ? 'ap-status-badge--active' : 'ap-status-badge--paused'}`}
            data-testid="ap-status-badge"
          >
            <span className="ap-status-dot" />
            {schedule.active ? 'Aktiv' : 'Pausad'}
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="tab-switcher" style={{ marginBottom: 32 }}>
        {[
          ['schema', 'Schema'],
          ['ko', 'Kö'],
          ['historik', 'Historik'],
          ['installningar', 'Inställningar'],
        ].map(([id, label]) => (
          <button
            key={id}
            className={`tab-btn${tab === id ? ' tab-btn--active' : ''}`}
            onClick={() => setTab(id)}
            data-testid={`ap-tab-${id}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Schema tab ────────────────────────────────────────────────────── */}
      {tab === 'schema' && (
        <div className="ap-schema-layout">
          {/* Active schedule card */}
          {!loadingSchedule && schedule && (
            <div className="ap-card ap-card--status" data-testid="ap-active-schedule-card">
              <div className="ap-card-header">
                <div className="ap-card-title">
                  <CheckCircle size={15} />
                  Aktivt schema
                </div>
                <button
                  className={`ap-toggle-btn ${schedule.active ? 'ap-toggle-btn--on' : 'ap-toggle-btn--off'}`}
                  onClick={handleToggleActive}
                  data-testid="ap-toggle-active"
                >
                  {schedule.active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                  {schedule.active ? 'Aktivt' : 'Pausat'}
                </button>
              </div>
              <div className="ap-schedule-info">
                <div className="ap-info-row">
                  <Clock size={13} />
                  <span>Körs: <strong>{scheduleWhen}</strong></span>
                </div>
                <div className="ap-info-row">
                  <Globe size={13} />
                  <span>Marknader: <strong>{scheduleMarkets}</strong></span>
                </div>
                {schedule.next_run && (
                  <div className="ap-info-row">
                    <Calendar size={13} />
                    <span>
                      Nästa körning:{' '}
                      <strong>
                        {new Date(schedule.next_run).toLocaleString('sv-SE', {
                          dateStyle: 'medium', timeStyle: 'short',
                        })}
                      </strong>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Schedule form */}
          <div className="ap-card" data-testid="ap-schedule-form">
            <div className="ap-card-header">
              <div className="ap-card-title">
                <Calendar size={15} />
                {schedule ? 'Redigera schema' : 'Konfigurera schema'}
              </div>
            </div>

            <div className="form-stack" style={{ marginTop: 20 }}>
              <div className="field">
                <div className="field-label">Intervall</div>
                <div className="ap-interval-row">
                  {[['daily', 'Dagligen'], ['weekly', 'Veckovis'], ['custom', 'Anpassat']].map(([val, lbl]) => (
                    <label
                      key={val}
                      className={`ap-radio-card${interval === val ? ' ap-radio-card--on' : ''}`}
                      data-testid={`ap-interval-${val}`}
                    >
                      <input
                        type="radio"
                        name="ap-interval"
                        checked={interval === val}
                        onChange={() => setIntervalVal(val)}
                        style={{ display: 'none' }}
                      />
                      {lbl}
                    </label>
                  ))}
                </div>
                {interval === 'custom' && (
                  <div className="ap-days-grid" data-testid="ap-days-grid">
                    {ALL_DAYS.map(d => (
                      <label
                        key={d}
                        className={`ap-day-btn${days.includes(d) ? ' ap-day-btn--on' : ''}`}
                        data-testid={`ap-day-${d}`}
                      >
                        <input
                          type="checkbox"
                          checked={days.includes(d)}
                          onChange={() =>
                            setDays(prev =>
                              prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]
                            )
                          }
                          style={{ display: 'none' }}
                        />
                        {DAYS_SE[d].slice(0, 3)}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="field">
                <label className="field-label">
                  <Clock size={13} className="label-icon" />
                  Tid
                </label>
                <input
                  type="time"
                  className="field-input"
                  value={time}
                  onChange={e => setTime(e.target.value)}
                  data-testid="ap-time-input"
                  style={{ maxWidth: 140 }}
                />
              </div>

              <MarketCheckboxGroup
                selected={markets}
                onChange={setMarkets}
                label="Marknader"
                testIdPrefix="ap-sched"
              />
              <FilterRadio value={filter} onChange={setFilter} testIdPrefix="ap-sched" />
              <CategoryDropdown value={selCat} onChange={setSelCat} testIdPrefix="ap-sched" />

              <button
                className={`generate-btn${markets.length > 0 && !saving ? ' generate-btn--active' : ''}`}
                onClick={handleSaveSchedule}
                disabled={saving || markets.length === 0}
                data-testid="ap-save-schedule-btn"
                style={{ marginTop: 4 }}
              >
                {saving
                  ? <><span className="spinner" /> Sparar...</>
                  : schedule ? 'Uppdatera schema' : 'Spara schema'
                }
              </button>
            </div>
          </div>

          {/* Wizard card */}
          <div className="ap-card" data-testid="ap-wizard-card">
            <div className="ap-card-header">
              <div className="ap-card-title">
                <PlayCircle size={15} />
                Kör pipeline nu
              </div>
            </div>
            <p className="ap-card-desc">
              Interaktivt flöde — skanna Coming-produkter, välj vilka som ska bearbetas,
              skrapa produktdata, granska bullets och generera texter per marknad.
            </p>

            {wzStep !== 'idle' && <WizardStepper step={wzStep === 'scanning' ? 'scan' : wzStep === 'generating' ? 'generate' : wzStep === 'done' ? 'generate' : wzStep} />}

            {/* Progress log */}
            {wzMessages.length > 0 && wzStep !== 'done' && (
              <div className="ap-pipeline-progress" data-testid="ap-wizard-progress">
                <div className="ap-progress-log">
                  {wzMessages.map((msg, i) => (
                    <div key={i} className="ap-progress-row">
                      {(wzStep === 'scanning' || wzStep === 'scraping' || wzStep === 'generating')
                        && i === wzMessages.length - 1
                        ? <Loader2 size={12} className="ap-progress-spin" />
                        : <span className="ap-progress-dot" />}
                      <span>{msg}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Done banner */}
            {wzStep === 'done' && wzDone && (
              <div className="ap-pipeline-done" data-testid="ap-wizard-done" style={{ marginTop: 16 }}>
                <CheckCircle2 size={16} />
                <span>
                  Klar — {wzDone.generated} produkt{wzDone.generated !== 1 ? 'er' : ''} sparad{wzDone.generated !== 1 ? 'e' : ''} i kön
                </span>
                {wzDone.failed > 0 && (
                  <span className="ap-pipeline-failed">{wzDone.failed} misslyckade</span>
                )}
              </div>
            )}

            {/* Step: idle → form */}
            {wzStep === 'idle' && (
              <div className="form-stack" style={{ marginTop: 20 }}>
                <MarketCheckboxGroup
                  selected={wzMarkets}
                  onChange={setWzMarkets}
                  label="Marknader"
                  testIdPrefix="ap-wz"
                />
                <FilterRadio value={wzFilter} onChange={setWzFilter} testIdPrefix="ap-wz" />
                <CategoryDropdown value={wzCat} onChange={setWzCat} testIdPrefix="ap-wz" />
                <button
                  className={`generate-btn${wzMarkets.length > 0 ? ' generate-btn--active' : ''}`}
                  onClick={handleScan}
                  disabled={wzMarkets.length === 0}
                  data-testid="ap-wz-start-btn"
                  style={{ marginTop: 4 }}
                >
                  <Search size={15} />
                  Skanna Coming-produkter
                </button>
              </div>
            )}

            {/* Step: list */}
            {wzStep === 'list' && (
              <div style={{ marginTop: 16 }}>
                {wzSummary && (
                  <div className="ap-scan-summary" data-testid="ap-scan-summary">
                    <div><strong>{wzSummary.total_skus}</strong> SKUs</div>
                    <div><strong>{wzSummary.total_products}</strong> unika produkter (grupperade på Code)</div>
                    <div>Markerade: <strong>{selectedCount}</strong></div>
                  </div>
                )}

                {wzProducts.length === 0 ? (
                  <div className="ap-pipeline-error" data-testid="ap-empty-scan">
                    <XCircle size={16} />
                    <span>Inga produkter hittades som matchar filtret</span>
                  </div>
                ) : (
                  <div className="ap-product-table-wrap" data-testid="ap-product-table">
                    <table className="ap-product-table">
                      <thead>
                        <tr>
                          <th style={{ width: 40 }}>
                            <input
                              type="checkbox"
                              checked={allProductsSelected}
                              onChange={(e) => {
                                const next = {};
                                if (e.target.checked) wzProducts.forEach(p => { next[p.code] = true; });
                                setWzSelected(next);
                              }}
                              data-testid="ap-select-all-products"
                            />
                          </th>
                          <th>Code</th>
                          <th>Namn</th>
                          <th>Kategori</th>
                          <th>SKUs</th>
                          <th>EAN</th>
                          <th>Tillverkare</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wzProducts.map(p => (
                          <tr
                            key={p.code}
                            className={wzSelected[p.code] ? 'ap-product-row--selected' : ''}
                            data-testid={`ap-product-row-${p.code}`}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={!!wzSelected[p.code]}
                                onChange={() => setWzSelected(prev => ({ ...prev, [p.code]: !prev[p.code] }))}
                                data-testid={`ap-product-check-${p.code}`}
                              />
                            </td>
                            <td><code style={{ fontSize: 11 }}>{p.code}</code></td>
                            <td>{p.name}</td>
                            <td>
                              {p.category_name
                                ? <span className="ap-cat-badge">{p.category_name}</span>
                                : <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>}
                            </td>
                            <td>{p.sku_count}</td>
                            <td style={{ fontSize: 11 }}>{(p.eans && p.eans[0]) || '—'}</td>
                            <td>
                              {p.manufacturer_code
                                ? <span className="ap-mfr-chip">{p.manufacturer_code}</span>
                                : <span className="ap-mfr-missing">Saknas</span>
                              }
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="ap-step-actions">
                  <button className="ap-btn-secondary" onClick={handleWizardReset} data-testid="ap-wz-cancel">
                    Avbryt
                  </button>
                  <button
                    className={`generate-btn${selectedCount > 0 ? ' generate-btn--active' : ''}`}
                    onClick={handleScrape}
                    disabled={selectedCount === 0}
                    data-testid="ap-wz-scrape-btn"
                  >
                    <Sparkles size={15} />
                    Skrapa {selectedCount} produkt{selectedCount !== 1 ? 'er' : ''}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Step: review */}
            {wzStep === 'review' && (
              <div style={{ marginTop: 16 }}>
                <p className="ap-card-desc" style={{ marginTop: 0 }}>
                  Granska och redigera bullets innan textgenerering. Produkter utan bullets hoppas över.
                </p>
                <div className="ap-review-list" data-testid="ap-review-list">
                  {wzProducts.filter(p => wzSelected[p.code]).map(p => {
                    const sc = wzScraped[p.code] || {};
                    const failed = !sc.bullets;
                    const lowConf = !failed && sc.status === 'low_confidence';
                    const cardCls = failed
                      ? 'ap-review-card ap-review-card--failed'
                      : lowConf
                        ? 'ap-review-card ap-review-card--warn'
                        : 'ap-review-card';
                    const statusCls = failed
                      ? 'ap-review-status--failed'
                      : lowConf
                        ? 'ap-review-status--warn'
                        : 'ap-review-status--ok';
                    const statusLabel = failed
                      ? (sc.status === 'no_ean' ? 'EAN saknas' : 'Misslyckades')
                      : lowConf
                        ? `Låg tillit (${sc.source_urls?.length || 0} källa)`
                        : `Klart (${sc.source_urls?.length || 0} källor)`;
                    return (
                      <div
                        key={p.code}
                        className={cardCls}
                        data-testid={`ap-review-card-${p.code}`}
                      >
                        <div className="ap-review-header">
                          <div>
                            <div className="ap-review-name">{p.name}</div>
                            <div className="ap-review-meta">
                              <code>{p.code}</code>
                              {p.eans && p.eans[0] && <> · EAN: {p.eans[0]}</>}
                              {p.manufacturer_code && <> · {p.manufacturer_code}</>}
                              {sc.confidence && <> · confidence: {sc.confidence}</>}
                            </div>
                          </div>
                          <span
                            className={`ap-review-status ${statusCls}`}
                            data-testid={`ap-review-status-${p.code}`}
                          >
                            {statusLabel}
                          </span>
                        </div>
                        <textarea
                          className="ap-review-bullets"
                          value={sc.bullets || ''}
                          placeholder={failed ? 'Ingen data hittades — skriv in bullets manuellt för att inkludera' : ''}
                          onChange={(e) =>
                            setWzScraped(prev => ({
                              ...prev,
                              [p.code]: { ...(prev[p.code] || {}), bullets: e.target.value },
                            }))
                          }
                          data-testid={`ap-bullets-${p.code}`}
                        />
                        {sc.source_urls && sc.source_urls.length > 0 && (
                          <div className="ap-review-sources">
                            Källor:
                            {sc.source_urls.slice(0, 4).map((u, i) => (
                              <a key={i} href={u} target="_blank" rel="noopener noreferrer">
                                {(() => { try { return new URL(u).hostname; } catch { return u.slice(0, 40); } })()}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="ap-step-actions">
                  <button className="ap-btn-secondary" onClick={handleWizardReset} data-testid="ap-wz-restart">
                    Börja om
                  </button>
                  <button
                    className="generate-btn generate-btn--active"
                    onClick={handleGenerate}
                    data-testid="ap-wz-generate-btn"
                  >
                    <PlayCircle size={15} />
                    Generera texter
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* Step: done */}
            {wzStep === 'done' && (
              <div className="ap-step-actions" style={{ marginTop: 16 }}>
                <button
                  className="generate-btn generate-btn--active"
                  onClick={handleWizardReset}
                  data-testid="ap-wz-new-run"
                >
                  Kör igen
                </button>
              </div>
            )}

            {/* Cancel mid-run */}
            {(wzStep === 'scanning' || wzStep === 'scraping' || wzStep === 'generating') && (
              <div className="ap-step-actions">
                <button
                  className="ap-btn-danger"
                  onClick={handleWizardReset}
                  data-testid="ap-wz-abort"
                >
                  Avbryt körning
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Kö tab ────────────────────────────────────────────────────────── */}
      {tab === 'ko' && <QueueTab />}

      {/* ── Historik tab ──────────────────────────────────────────────────── */}
      {tab === 'historik' && <HistoryTab />}

      {/* ── Inställningar tab ─────────────────────────────────────────────── */}
      {tab === 'installningar' && (
        <div className="ap-settings-card" data-testid="ap-settings-card">
          <div className="ap-card-header">
            <div className="ap-card-title">
              <SettingsIcon size={15} />
              Autopilot-inställningar
            </div>
            {savingSettings && <span style={{ fontSize: 11, color: '#64748b' }}>Sparar...</span>}
          </div>

          {!settingsLoaded ? (
            <div style={{ padding: 20, color: '#64748b' }}>Laddar inställningar...</div>
          ) : (
            <>
              <div className="ap-settings-row">
                <div>
                  <div className="ap-settings-label">Auto-skrapning efter skanning</div>
                  <div className="ap-settings-hint">
                    Hoppar automatiskt från produktlista till skrapning. Avstäng för manuell kontroll.
                  </div>
                </div>
                <button
                  className={`ap-toggle-switch${settings.auto_scrape ? ' ap-toggle-switch--on' : ''}`}
                  onClick={() => updateSetting('auto_scrape', !settings.auto_scrape)}
                  data-testid="ap-setting-auto-scrape"
                  aria-label="Auto-skrapning"
                />
              </div>

              <div className="ap-settings-row">
                <div>
                  <div className="ap-settings-label">Auto-generering</div>
                  <div className="ap-settings-hint">
                    Hoppar över granskningssteget och genererar texter direkt från skrapade bullets.
                    Lämpligt för schemalagda körningar.
                  </div>
                </div>
                <button
                  className={`ap-toggle-switch${settings.auto_generate ? ' ap-toggle-switch--on' : ''}`}
                  onClick={() => updateSetting('auto_generate', !settings.auto_generate)}
                  data-testid="ap-setting-auto-generate"
                  aria-label="Auto-generering"
                />
              </div>

              <div className="ap-settings-row">
                <div>
                  <div className="ap-settings-label">Max produkter per körning</div>
                  <div className="ap-settings-hint">
                    Tak för hur många unika produkter som hämtas i varje skanning (0 = ingen gräns).
                  </div>
                </div>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  className="ap-settings-input"
                  value={settings.max_products_per_run}
                  onChange={(e) => setSettings(prev => ({ ...prev, max_products_per_run: parseInt(e.target.value, 10) || 0 }))}
                  onBlur={() => saveSettings(settings)}
                  data-testid="ap-setting-max-products"
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
